﻿import { LiveDataSet, LiveDataSetValue } from "../../API/LiveData/LiveDataSet";
import { ParameterSchemaStation } from "../../API/Schema/ParameterSchema";
import { IUnitsConversionSet } from "../../Converters/UnitsConverter/IUnitsConverter";
import { DefaultGroupFormatter } from "../../Formatters/DefaultGroupFormatter";
import { ILiveDataListenerContext, ILiveDataListener } from "../../ILiveDataListener";
import { MainSite } from "../../MainSite";
import { WarningGroup } from "../../Model/WarningGroup";
import { ParameterGroupInfoSet } from "../../ParameterGroupInfoSet";
import { ProfileComponent, Profile } from "../../ValueTypes/Profile";
import { ProfileTypeEnum } from "../Components/ProfileChartComponent";
import { DirectionalPolarChart } from "./DirectionalPolarChart";
import { ChartDataPoint, HovmollerChart } from "./HovmollerChart";
import { ISiteObservationsPage } from "./ISiteObservationsPage";
import { ProfileChart } from "./ProfileChart";
import { ProgressiveVectorDiagram } from "./ProgressiveVectorDiagram";
import { ValueBox } from "./ValueBox";

export enum SpeedUnits {
    kts,
    mps,
}

export class Current implements ISiteObservationsPage {
    public id: string = "current";
    public label: string = "Current";

    public station: ParameterSchemaStation;
    public warningGroup: WarningGroup;
    private _dpc: DirectionalPolarChart;
    private _pvd: ProgressiveVectorDiagram;
    private _chartsHovmoller: HovmollerChart;
    private _chartsHovmoller2: HovmollerChart;
    private _chartsHovmollerHorizontal: HovmollerChart;
    private currentProfileSensor?: string = null; //: for Color Flood chart..
    private currentProfileParam?: string = null; //: for Color Flood chart..
    private currentProfileHorizontalSensor?: string = null; //: for Color Flood chart..
    private currentProfileHorizontalParam?: string = null; //: for Color Flood chart..

    public notifyResize() {}

    public show(station: ParameterSchemaStation): boolean | ISiteObservationsPage[] {
        if (station.Station.Region == "GOM") {
            // GOM has a specified Current page (GOMCurrentPage.ts), so don't show this one
            return false;
        }

        const pages = [];

        // Go through all sensorIds
        for (let sensorId in station.Sensors) {
            // if not useLiveData or forecast, skip
            if (
                !station.Sensors[sensorId].UseLiveData ||
                sensorId.toLowerCase().includes("forecast")
            ) {
                continue;
            }

            const sensor = station.Sensors[sensorId];
            if (sensorId.includes("orecast") || sensorId.includes("Diagnostics")) {
                continue;
            }

            // Check if the sensorId has any wave Current parameters. If so, generate a Currents-pages for this sensorId
            for (let paramId in sensor.Parameters) {
                if (paramId.indexOf("Current") != -1) {
                    const label =
                        station.Sensors[sensorId].SensorDefinition?.DisplayName ?? sensorId;
                    pages.push(new Current(this.mainSite, this.ctx, sensorId, label));

                    break;
                }
            }
        }

        if (pages.length == 1) {
            return true;
        } else if (pages.length > 1) {
            return pages;
        }
        return false;
    }

    constructor(
        public mainSite: MainSite,
        private ctx: ILiveDataListenerContext,
        private sensorId: string,
        label: string,
    ) {
        // if sensorId is not set to null, update label and id to distinguish the pages
        if (this.sensorId !== null) {
            this.label += ` (${label})`;
            this.id += `${this.sensorId}`;
        }
    }

    public render(
        container: JQuery,
        listeners: ILiveDataListener[],
        station: ParameterSchemaStation,
        warningGroup: WarningGroup,
    ): void {
        // Page instance is reused across stations; clear sensor IDs detected for the previous one
        this.currentProfileSensor = null;
        this.currentProfileParam = null;
        this.currentProfileHorizontalSensor = null;
        this.currentProfileHorizontalParam = null;

        this.station = station;
        this.warningGroup = warningGroup;

        /*
         * Quick and dirty fix to detect if current speed is shown as kts rather than mps.
         * It would be much better to allow the DefaultGroupFormatter to choose the group from
         * multiple options based on what is available.
         */
        let currentSpeedUnits = SpeedUnits.mps;
        let hasHorizontal = false;
        for (const sensorId in station.Sensors) {
            const sensor = station.Sensors[sensorId];
            if (Object.keys(sensor.Parameters).some((pid) => pid == "CurrentProfile")) {
                /*if (!this.currentProfileParam) {
                    this.currentProfileSensor = sensorId;
                    this.currentProfileParam = "CurrentProfile";
                }*/
                // break;
            }
            if (Object.keys(sensor.Parameters).some((pid) => pid == "CurrentProfile/kts")) {
                currentSpeedUnits = SpeedUnits.kts;
                /*if (!this.currentProfileParam) {
                    this.currentProfileSensor = sensorId;
                    this.currentProfileParam = "CurrentProfile/kts";
                }*/
                // break;
            }
            if (
                Object.keys(sensor.Parameters).some(
                    (pid) => pid.indexOf("HorizontalCurrentProfile") === 0,
                )
            ) {
                hasHorizontal = true;
                /*if (!this.currentProfileHorizontalParam) {
                    this.currentProfileHorizontalSensor = sensorId;
                    this.currentProfileHorizontalParam = "HorizontalCurrentProfile";
                }*/
                // break;
            }
        }

        var page = $('<div class="midi_SiteObservations_padded_page_content"></div>');

        var leftPanel = $("<div></div>")
            .css("position", "absolute")
            .css("height", "47em")
            .css("width", "15em")
            .appendTo(page);

        var centrePanel = $("<div></div>")
            .css("position", "absolute")
            .css("height", "47em")
            .css("left", "16em")
            .css("width", "15em")
            .appendTo(page);

        var rightPanel = $("<div></div>")
            .css("position", "absolute")
            .css("height", "47em")
            .css("left", "32em")
            .css("width", "32em")
            .appendTo(page);

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(
                    ParameterGroupInfoSet.Total_CurrentDirection_Surface_deg_Mean,
                    this.sensorId,
                ),
                0,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(
                    ParameterGroupInfoSet.Total_CurrentDirection_Middle_deg_Mean,
                    this.sensorId,
                ),
                20.2,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(
                    ParameterGroupInfoSet.Total_CurrentDirection_Bottom_deg_Mean,
                    this.sensorId,
                ),
                40.4,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(
                    ParameterGroupInfoSet.Total_CurrentDirection_Peak_deg_Mean,
                    this.sensorId,
                ),
                60.6,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                centrePanel,
                new DefaultGroupFormatter(
                    currentSpeedUnits == SpeedUnits.mps
                        ? ParameterGroupInfoSet.Total_CurrentSpeed_Surface_mps_Mean
                        : ParameterGroupInfoSet.Total_CurrentSpeed_Surface_kts_Mean,
                    this.sensorId,
                ),
                0,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                centrePanel,
                new DefaultGroupFormatter(
                    currentSpeedUnits == SpeedUnits.mps
                        ? ParameterGroupInfoSet.Total_CurrentSpeed_Middle_mps_Mean
                        : ParameterGroupInfoSet.Total_CurrentSpeed_Middle_kts_Mean,
                    this.sensorId,
                ),
                20.2,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                centrePanel,
                new DefaultGroupFormatter(
                    currentSpeedUnits == SpeedUnits.mps
                        ? ParameterGroupInfoSet.Total_CurrentSpeed_Bottom_mps_Mean
                        : ParameterGroupInfoSet.Total_CurrentSpeed_Bottom_kts_Mean,
                    this.sensorId,
                ),
                40.4,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                centrePanel,
                new DefaultGroupFormatter(
                    currentSpeedUnits == SpeedUnits.mps
                        ? ParameterGroupInfoSet.Total_CurrentSpeed_Peak_mps_Mean
                        : ParameterGroupInfoSet.Total_CurrentSpeed_Peak_kts_Mean,
                    this.sensorId,
                ),
                60.6,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                centrePanel,
                new DefaultGroupFormatter(
                    ParameterGroupInfoSet.MeasurementDepth_Total_Current_Peak_deg_Mean,
                    this.sensorId,
                ),
                80.8,
                0,
                19.2,
                100,
                this.ctx,
            ),
        );

        let chartSelectTabs = $('<div class="current_chart_selector"></div>').appendTo(rightPanel);

        let currentBox = $(
            '<div class="site-obs-current-charts" data-chart-type="vertical"></div>',
        )
            .appendTo(rightPanel)
            .hide();
        let currentBoxHorizontal = $(
            '<div class="site-obs-current-charts" data-chart-type="horizontal"></div>',
        )
            .appendTo(rightPanel)
            .hide();
        let pvdBox = $('<div class="site-obs-current-charts" data-chart-type="pvd"></div>')
            .appendTo(rightPanel)
            .hide();
        let polarBox = $('<div class="site-obs-current-charts" data-chart-type="polar"></div>')
            .appendTo(rightPanel)
            .hide();
        let hovmollerBoxVertical = $(
            '<div class="site-obs-current-charts" data-chart-type="hovmoller"><em class="text-muted">Loading Color Flood chart...</em></div>',
        )
            .appendTo(rightPanel)
            .hide();
        let hovmollerBoxHorizontal = $(
            '<div class="site-obs-current-charts" data-chart-type="hovmoller-horizontal"><em class="text-muted">Loading Color Flood chart...</em></div>',
        )
            .appendTo(rightPanel)
            .hide();

        let showChart = (chartType: string, label: string) => {
            /*spectrumBox.hide();
            polarSpectrumBox.hide();
            polarBox.hide();
            hovmoller01Box.hide();*/
            $(".site-obs-current-charts").hide();
            $(`.site-obs-current-charts[data-chart-type="${chartType}"]`).show();
            $(".site-obs-current-chart-select-label").text(label);
            MainSite.instance.dispatchResize();
        };

        chartSelectTabs.append("<span>Chart: </span>");
        let $buttonGroup: JQuery = $('<div class="btn-group"></div>').appendTo(chartSelectTabs);
        $(
            '<button type="button" class="btn btn-default dropdown-toggle" data-toggle="dropdown"><span class="site-obs-current-chart-select-label">Select chart..</span> <span class="caret"></span></button>',
        ).appendTo($buttonGroup);
        let tpul = $("<ul></ul>")
            .addClass("dropdown-menu")
            // .append($('<span></span>').text("Chart: "))
            .appendTo($buttonGroup);

        this._dpc = new DirectionalPolarChart(
            this.mainSite,
            this,
            polarBox,
            5,
            0,
            93,
            100,
            currentSpeedUnits == SpeedUnits.mps
                ? ParameterGroupInfoSet.Total_CurrentSpeed_Surface_mps_Mean.Id
                : ParameterGroupInfoSet.Total_CurrentSpeed_Surface_kts_Mean.Id,
            ParameterGroupInfoSet.Total_CurrentDirection_Surface_deg_Mean.Id,
            true,
            this.sensorId,
        );

        listeners.push(this._dpc);

        this._pvd = new ProgressiveVectorDiagram(
            this.mainSite,
            this,
            pvdBox,
            5,
            0,
            93,
            100,
            currentSpeedUnits,
            this.sensorId ?? undefined,
        );

        listeners.push(this._pvd);

        listeners.push(
            new ProfileChart(
                this,
                currentBox,
                5,
                0,
                93,
                100,
                currentSpeedUnits,
                "Current",
                ProfileTypeEnum.Depth,
                this.sensorId,
            ),
        );

        listeners.push(
            new ProfileChart(
                this,
                currentBoxHorizontal,
                5,
                0,
                93,
                100,
                currentSpeedUnits,
                "Current",
                ProfileTypeEnum.Horizontal,
                this.sensorId,
            ),
        );

        //: get unitConversionSet's and factors (ucf_ prefixed vars = "unit conversion factor"):
        var speedUnitsStr = currentSpeedUnits == SpeedUnits.mps ? "m/s" : "kts";
        var unitsStrDepthOrHeight = "m";
        let unitsConversionSet_DepthOrHeight: IUnitsConversionSet | null =
            MainSite.getUnitConverter().getConversionSet("m");
        let unitsConversionSet_Speed: IUnitsConversionSet | null =
            MainSite.getUnitConverter().getConversionSet(speedUnitsStr);
        let ucf_DepthOrHeight: (value: any) => any = (value: any) => value;
        let ucf_Speed: (value: any) => any = (value: any) => value;
        let ucf_Direction: (value: any) => any = (value: any) => value;
        if (unitsConversionSet_DepthOrHeight) {
            ucf_DepthOrHeight = unitsConversionSet_DepthOrHeight.convertValue;
            unitsStrDepthOrHeight = unitsConversionSet_DepthOrHeight.convertedUnits;
        }
        if (unitsConversionSet_Speed) {
            ucf_Speed = unitsConversionSet_Speed.convertValue;
            speedUnitsStr = unitsConversionSet_Speed.convertedUnits;
        }

        // Speed: Options specific to speed chart
        let hovmolOptionsSpeed: __Highcharts.Options = HovmollerChart.chartOptionsDefault();
        hovmolOptionsSpeed.tooltip = {
            headerFormat: `Speed (${speedUnitsStr})<br/>`,
            pointFormat: `{point.x:%d/%m/%Y %H:%M} (<em>{point.y:.1f}${unitsStrDepthOrHeight}</em>): <b>{point.value:.1f}</b>`, //: https://highcharts.com/class-reference/Highcharts.Time#dateFormat
        };
        hovmolOptionsSpeed.yAxis = {
            ...hovmolOptionsSpeed.yAxis,
            title: {
                text: `Depth (${unitsStrDepthOrHeight})`,
            },
            reversed: true,
        };
        hovmolOptionsSpeed.colorAxis = {
            ...hovmolOptionsSpeed.colorAxis,
            stops: [
                // Jet colorscheme
                [0, "#0000FF"], // Blue
                [0.33, "#00FFFF"], // Cyan
                [0.66, "#FFFF00"], // Yellow
                [1, "#FF0000"], // Red
            ],
        };

        // Direction: Options specific to direction chart
        let hovmolOptionsDirection: __Highcharts.Options = HovmollerChart.chartOptionsDefault();
        hovmolOptionsDirection.tooltip = {
            headerFormat: `Direction (deg)<br/>`,
            pointFormat: `{point.x:%d/%m/%Y %H:%M} (<em>{point.y:.1f}${unitsStrDepthOrHeight}</em>): <b>{point.value:.1f}</b>`, //: https://highcharts.com/class-reference/Highcharts.Time#dateFormat
        };
        hovmolOptionsDirection.yAxis = {
            ...hovmolOptionsDirection.yAxis,
            title: {
                text: `Depth (${unitsStrDepthOrHeight})`,
            },
            reversed: true,
        };
        hovmolOptionsDirection.colorAxis = {
            ...hovmolOptionsDirection.colorAxis,
            stops: [
                [0.0, "rgb(255, 0, 0)"],
                [0.25, "rgb(160, 32, 240)"],
                [0.5, "rgb(0, 0, 255)"],
                [0.75, "rgb(252, 186, 3)"],
                [1.0, "rgb(255, 0, 0)"],
            ],
            tickPositions: [0, 90, 180, 270, 360],
            startOnTick: true,
            endOnTick: true,
        };

        this._chartsHovmoller = new HovmollerChart(
            //// mainSite
            this.mainSite,
            //// page
            this,
            //// $container
            hovmollerBoxVertical,
            //// top, left, height, width
            0,
            0,
            93,
            100,
            //// specific chart options
            hovmolOptionsSpeed,
            hovmolOptionsDirection,
            //// getDataMethod
            (
                tsFrom: moment.Moment,
                tsTo: moment.Moment,
                isSpeed: boolean,
                extractProfileCmpValue: (cp: ProfileComponent) => number,
                chart: HovmollerChart,
                callback: (
                    data: ChartDataPoint[],
                    isFailed: boolean,
                    failedMessage?: string,
                ) => void,
            ): void => {
                let res: ChartDataPoint[] = [];

                if (this.currentProfileSensor == null || this.currentProfileParam == null) {
                    console.warn(
                        "Current Hovmoller (Vertical) chart: no current profile sensor or param detected for this station.",
                    );
                    callback([], false);
                    return;
                }

                let sensor = this.currentProfileSensor;
                let parameter = this.currentProfileParam;
                let uri = `/api/v1/HistoricalData/P/${this.station.Station.Id}/${encodeURIComponent(sensor)}/${encodeURIComponent(parameter)}?fromUnixMillis=${tsFrom.valueOf()}&toUnixMillis=${tsTo.valueOf()}`;
                console.info("Current Hovmoller (Vertical) - data fetch URI:", uri);

                $.ajax(uri + "", {
                    cache: false,
                    dataType: "json",
                    type: "GET",
                    timeout: 30000,
                    success: (data: any, text: string, jqXHR: JQueryXHR) => {
                        try {
                            if (Array.isArray(data.results[0].series[0].values)) {
                                for (let i = 0; i < data.results[0].series[0].values.length; i++) {
                                    let x = data.results[0].series[0].values[i];
                                    let m: moment.Moment = moment.utc(
                                        x[0],
                                        "YYYY-MM-DD HH:mm:ssZ",
                                        true,
                                    );
                                    if (m && m.isValid()) {
                                        let profile = JSON.parse(x[1]) as Profile;

                                        let components = profile.Components.sort(
                                            (a, b) => b.Depth - a.Depth,
                                        ); //: TODO: check sorting!!
                                        components.forEach((cp, cpi) => {
                                            if (
                                                !cp.Speed ||
                                                isNaN(cp.Speed) ||
                                                !isFinite(cp.Speed) ||
                                                cp.Speed < 0 ||
                                                cp.Speed > 10
                                            ) {
                                                return;
                                            }
                                            res.push({
                                                timestamp: m.valueOf(),
                                                y: ucf_DepthOrHeight(cp.Depth),
                                                val: isSpeed ? ucf_Speed(cp.Speed) : cp.Direction,
                                            });
                                        });
                                    } else {
                                        console.warn(
                                            "Unable to parse " +
                                                this.currentProfileParam +
                                                " Profile Data from server response. Error: Timestamp not parseable to a valid Moment.",
                                        );
                                        // this.drawChartMessage("Error while retrieving " + this.profileName + " Profile Data.");
                                    }
                                }

                                callback(res, false);
                            }
                        } catch (err) {
                            console.warn(
                                "Unable to parse " +
                                    this.currentProfileParam +
                                    " Profile Data from server response. Error: ",
                                err,
                            );
                            callback([], true);
                        }
                    },
                    error: (jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
                        console.warn(
                            "Unable to parse " +
                                this.currentProfileParam +
                                " Profile Data from server response. Error: ",
                            errorThrown,
                        );
                        callback([], true);
                    },
                });
            },
            // to help data fetching methods use livedata if necessary (e.g. to determine sensors/params IDs..)
            (latestData: LiveDataSet, chart: HovmollerChart): void => {
                if (this.currentProfileParam !== null) {
                    chart.updateChart();
                    return; //: already dealt with.
                }

                let liveDataSetValue: LiveDataSetValue = null;
                if (latestData.Stations[this.station.Station.Id]) {
                    if (
                        Object.keys(latestData.Stations[this.station.Station.Id].GroupValues).some(
                            (pid) => pid == "CurrentProfile",
                        )
                    ) {
                        liveDataSetValue =
                            latestData.Stations[this.station.Station.Id].GroupValues[
                                "CurrentProfile"
                            ][0];
                        this.currentProfileSensor = liveDataSetValue.Sensor;
                        this.currentProfileParam = "CurrentProfile";
                    }
                }

                chart.updateChart();
            },
        );
        listeners.push(this._chartsHovmoller);

        if (hasHorizontal) {
            let hovmolOptionsSpeedHorizontal: __Highcharts.Options = {
                ...hovmolOptionsSpeed,
            };
            hovmolOptionsSpeedHorizontal.yAxis = {
                ...hovmolOptionsSpeedHorizontal.yAxis,
                title: {
                    text: `Distance (${unitsStrDepthOrHeight})`,
                },
                reversed: true,
            };

            this._chartsHovmollerHorizontal = new HovmollerChart(
                this.mainSite,
                this,
                hovmollerBoxHorizontal,
                0,
                0,
                93,
                100,
                //// specific chart options
                hovmolOptionsSpeedHorizontal,
                hovmolOptionsDirection,
                //// getDataMethod
                (
                    tsFrom: moment.Moment,
                    tsTo: moment.Moment,
                    isSpeed: boolean,
                    extractProfileCmpValue: (cp: ProfileComponent) => number,
                    chart: HovmollerChart,
                    callback: (
                        data: ChartDataPoint[],
                        isFailed: boolean,
                        failedMessage?: string,
                    ) => void,
                ): void => {
                    let res: ChartDataPoint[] = [];

                    if (
                        this.currentProfileHorizontalSensor == null ||
                        this.currentProfileHorizontalParam == null
                    ) {
                        console.warn(
                            "Current Hovmoller (Horizontal) chart: no current profile sensor or param detected for this station.",
                        );
                        callback([], false);
                        return;
                    }

                    let sensor = this.currentProfileHorizontalSensor;
                    let parameter = this.currentProfileHorizontalParam;
                    let uri = `/api/v1/HistoricalData/P/${this.station.Station.Id}/${encodeURIComponent(sensor)}/${encodeURIComponent(parameter)}?fromUnixMillis=${tsFrom.valueOf()}&toUnixMillis=${tsTo.valueOf()}`;
                    console.info("Current Hovmoller (Horizontal) - data fetch URI:", uri);

                    $.ajax(uri + "", {
                        cache: false,
                        dataType: "json",
                        type: "GET",
                        timeout: 30000,
                        success: (data: any, text: string, jqXHR: JQueryXHR) => {
                            try {
                                if (Array.isArray(data.results[0].series[0].values)) {
                                    for (
                                        let i = 0;
                                        i < data.results[0].series[0].values.length;
                                        i++
                                    ) {
                                        let x = data.results[0].series[0].values[i];
                                        let m: moment.Moment = moment.utc(
                                            x[0],
                                            "YYYY-MM-DD HH:mm:ssZ",
                                            true,
                                        );
                                        if (m && m.isValid()) {
                                            let profile = JSON.parse(x[1]) as Profile;

                                            let components = profile.Components.sort(
                                                (a, b) =>
                                                    b.Distance ?? b.Depth - a.Distance ?? a.Depth,
                                            );
                                            components.forEach((cp, cpi) => {
                                                let val = extractProfileCmpValue(cp);

                                                if (!val || isNaN(val)) {
                                                    console.log("val: ", val);
                                                    return;
                                                }
                                                if (isSpeed) val = ucf_Speed(val);
                                                res.push({
                                                    timestamp: m.valueOf(),
                                                    y: ucf_DepthOrHeight(cp.Distance ?? cp.Depth),
                                                    val: val,
                                                });
                                            });
                                        } else {
                                            console.warn(
                                                "Unable to parse " +
                                                    this.currentProfileHorizontalParam +
                                                    " Profile Data from server response. Error: Timestamp not parseable to a valid Moment.",
                                            );
                                            // this.drawChartMessage("Error while retrieving " + this.profileName + " Profile Data.");
                                        }
                                    }

                                    callback(res, false);
                                }
                            } catch (err) {
                                console.warn(
                                    "Unable to parse " +
                                        this.currentProfileHorizontalParam +
                                        " Profile Data from server response. Error: ",
                                    err,
                                );
                                callback([], true);
                            }
                        },
                        error: (jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
                            console.warn(
                                "Unable to parse " +
                                    this.currentProfileHorizontalParam +
                                    " Profile Data from server response. Error: ",
                                errorThrown,
                            );
                            callback([], true);
                        },
                    });
                },
                // to help data fetching methods use livedata if necessary (e.g. to determine sensors/params IDs..)
                (latestData: LiveDataSet, chart: HovmollerChart): void => {
                    if (this.currentProfileHorizontalParam !== null) {
                        return; //: already dealt with.
                    }

                    let liveDataSetValue: LiveDataSetValue = null;
                    if (latestData.Stations[this.station.Station.Id]) {
                        if (
                            Object.keys(
                                latestData.Stations[this.station.Station.Id].GroupValues,
                            ).some((pid) => pid == "HorizontalCurrentProfile")
                        ) {
                            liveDataSetValue =
                                latestData.Stations[this.station.Station.Id].GroupValues[
                                    "HorizontalCurrentProfile"
                                ][0];
                            this.currentProfileHorizontalSensor = liveDataSetValue.Sensor;
                            this.currentProfileHorizontalParam = "HorizontalCurrentProfile";
                        }
                    }

                    chart.updateChart();
                },
            );
            listeners.push(this._chartsHovmollerHorizontal);
        }

        let tpulLiActiveClass = "active"; //: 'selected' prior to using drop-down
        let charts = [
            { key: "vertical", label: "Vertical Profile" },
            // { key: 'horizontal', label: 'Horizontal Profile' },
            { key: "pvd", label: "PVD" },
            { key: "polar", label: "Polar" },
            { key: "hovmoller", label: hasHorizontal ? "Color Flood (Vertical)" : "Color Flood" },
            // { key: 'hovmoller-horizontal', label: 'Color Flood (Horizontal)' },
        ];
        if (hasHorizontal) {
            charts = [
                { key: "vertical", label: "Vertical Profile" },
                { key: "horizontal", label: "Horizontal Profile" },
                { key: "pvd", label: "PVD" },
                { key: "polar", label: "Polar" },
                {
                    key: "hovmoller",
                    label: hasHorizontal ? "Color Flood (Vertical)" : "Color Flood",
                },
                //{ key: 'hovmoller-horizontal', label: 'Color Flood (Horizontal)' },
            ];
        }

        for (var i = 0; i < charts.length; i++) {
            let chart = charts[i];
            let $li = $(`<li class="dropdown-menu-item"></li>`);
            let $a = $(`<a href="">${chart.label}</a>`).appendTo($li);
            $a.on("click", (ev) => {
                ev.preventDefault();
                tpul.children("li").removeClass(tpulLiActiveClass);
                $li.addClass(tpulLiActiveClass);
                showChart(chart.key, chart.label);
            });
            tpul.append($li);
        }

        // currentBox.show();
        setTimeout(() => {
            //: somehow, if executed immediately, this would not display the correct box..
            tpul.children("li").first().find("a").trigger("click");
        }, 500);

        page.appendTo(container);
        //page resizes due to highcharts charts being drawn before the box size is fixed and
        //the chart subsequently not filling the available space
        MainSite.instance.dispatchResize();
    }
}
